import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {SearchService} from '../services/search.service';
import {debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {ProductSearchResult, ProductSuggestion, SUGGESTION_ITEM_TYPE} from '../models/search';
import {Product} from '../models/product';
import {LoaderService} from '../services/loader.service';
import {FEEDBACK_LOCATION} from '../models/feedback';

interface OnSuggestionSelected {
  item: ProductSuggestion;
}

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.sass'],
  encapsulation: ViewEncapsulation.None
})

export class SearchComponent implements OnInit, OnDestroy {
  public isShowSearchComponent = false;
  public isSearchingProducts = false;
  public productSuggestions: ProductSuggestion[] = [];
  public selectedProduct: Product;
  public searchResults$ = of(null);
  public isLoading = false;
  public FEEDBACK_LOCATION: typeof FEEDBACK_LOCATION = FEEDBACK_LOCATION;

  public get resultStatusMessage(): string {
    return this.productSuggestions && this.productSuggestions[0]
      ? `${this.productSuggestions.length} products shown`
      : 'Please, type more then 3 letters and select the product from the list.';
  }

  private onComponentDestroyed$: Subject<void> = new Subject();

  public inputFormatter = (suggestion: ProductSuggestion): string => {
    if (suggestion.itemType === SUGGESTION_ITEM_TYPE.PRODUCT_COMPONENT) {
      return suggestion.itemVersion == null
        ? suggestion.itemName + ' (component)'
        : suggestion.itemName + ' ' + suggestion.itemVersion + ' (component)';
    }
    return suggestion.itemVersion == null
      ? suggestion.itemName
      : suggestion.itemName + ' ' + suggestion.itemVersion;
  }

  public search = (text$: Observable<string>): Observable<ProductSuggestion[]> => {
    return text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.isSearchingProducts = true),
      switchMap((searchText: string) => {
        this.searchResults$ = of(null);

        if (searchText.length < 3) {
          return of([{
            itemName: null,
            itemType: SUGGESTION_ITEM_TYPE.PRODUCT,
            itemVersion: null,
            smartNumber: null,
            prodId: null
          }]);
        }
        return this.searchService.getProductSuggestions(searchText);
      }),
      map((productSuggestions: ProductSuggestion[]): ProductSuggestion[] => {
        this.isSearchingProducts = false;
        this.productSuggestions = productSuggestions;

        return productSuggestions;
      }),
      takeUntil(this.onComponentDestroyed$)
    );
  }

  constructor(private searchService: SearchService,
              private router: Router,
              private loaderService: LoaderService) {
  }

  public onSuggestionSelected($onSuggestionSelected: OnSuggestionSelected): void {
    this.isLoading = true;

    const selectedSuggestion: ProductSuggestion = $onSuggestionSelected.item;
    const searchForSelectedSuggested$: Observable<ProductSearchResult[]> = selectedSuggestion.itemType === SUGGESTION_ITEM_TYPE.PRODUCT_COMPONENT
      ? this.searchService.searchForSuggestedComponent(selectedSuggestion)
      : this.searchService.searchForSuggestedProduct(selectedSuggestion);

    if (selectedSuggestion.smartNumber) {
      this.router.navigate(['/product/' + selectedSuggestion.smartNumber]);
      return;
    }

    this.searchResults$ = searchForSelectedSuggested$
      .pipe(
        tap(() => this.isLoading = false)
      );
  }

  ngOnInit(): void {
    this.loaderService.onToggleLoader.pipe(
      takeUntil(this.onComponentDestroyed$),
    ).subscribe((loaderConfig): void => {
      if (loaderConfig && loaderConfig.busy !== true) {
        this.isShowSearchComponent = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.onComponentDestroyed$.next();
    this.onComponentDestroyed$.complete();
    this.onComponentDestroyed$.unsubscribe();
  }
}
